import type { WidgetLoginResponse } from '@teo/widget-shared';

/** localStorage key prefix — one entry per widget slug */
const STORAGE_KEY_PREFIX = 'teo_widget_auth_';

/** Re-login after this age even without 401 (Sanctum tokens are long-lived). */
export const WIDGET_AUTH_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Auth only — widget settings are always fetched from get-modules-settings. */
export type StoredWidgetAuth = {
  slug: string;
  visitorId: string;
  apiBase: string;
  sessionId: number;
  companyId: number;
  moduleType: number;
  token: string;
  threadId: number | null;
  cachedAt: string;
};

function storageKey(slug: string): string {
  return `${STORAGE_KEY_PREFIX}${slug}`;
}

function isStoredWidgetAuth(value: unknown): value is StoredWidgetAuth {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.slug === 'string' &&
    typeof record.visitorId === 'string' &&
    typeof record.apiBase === 'string' &&
    typeof record.sessionId === 'number' &&
    typeof record.companyId === 'number' &&
    typeof record.moduleType === 'number' &&
    typeof record.token === 'string' &&
    record.token.length > 0 &&
    (record.threadId === null || typeof record.threadId === 'number') &&
    typeof record.cachedAt === 'string'
  );
}

function isCacheFresh(cachedAt: string): boolean {
  const cachedMs = Date.parse(cachedAt);
  if (!Number.isFinite(cachedMs)) return false;
  return Date.now() - cachedMs <= WIDGET_AUTH_CACHE_MAX_AGE_MS;
}

export function storedAuthToLoginResponse(
  stored: StoredWidgetAuth,
): Omit<WidgetLoginResponse, 'settings'> {
  return {
    id: stored.sessionId,
    company_id: stored.companyId,
    module_type: stored.moduleType,
    token: stored.token,
    thread_id: stored.threadId,
  };
}

export function createStoredWidgetAuth(
  login: Pick<
    WidgetLoginResponse,
    'id' | 'company_id' | 'module_type' | 'token' | 'thread_id'
  >,
  slug: string,
  visitorId: string,
  apiBase: string,
): StoredWidgetAuth {
  return {
    slug,
    visitorId,
    apiBase: apiBase.replace(/\/$/, ''),
    sessionId: login.id,
    companyId: login.company_id,
    moduleType: login.module_type,
    token: login.token,
    threadId: login.thread_id,
    cachedAt: new Date().toISOString(),
  };
}

export function readStoredWidgetAuth(
  slug: string,
  visitorId: string,
  apiBase: string,
): StoredWidgetAuth | null {
  if (typeof window === 'undefined') return null;

  const normalizedApiBase = apiBase.replace(/\/$/, '');

  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredWidgetAuth(parsed)) {
      window.localStorage.removeItem(storageKey(slug));
      return null;
    }

    if (
      parsed.slug !== slug ||
      parsed.visitorId !== visitorId ||
      parsed.apiBase !== normalizedApiBase
    ) {
      return null;
    }

    if (!isCacheFresh(parsed.cachedAt)) {
      window.localStorage.removeItem(storageKey(slug));
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredWidgetAuth(auth: StoredWidgetAuth): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey(auth.slug), JSON.stringify(auth));
  } catch {
    // Private mode / quota — in-memory session still works until refresh
  }
}

export function patchStoredWidgetAuthThreadId(
  slug: string,
  threadId: number,
): void {
  if (typeof window === 'undefined') return;

  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredWidgetAuth(parsed) || parsed.slug !== slug) return;

    writeStoredWidgetAuth({
      ...parsed,
      threadId,
      cachedAt: parsed.cachedAt,
    });
  } catch {
    // ignore
  }
}

export function clearStoredWidgetAuth(slug: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(storageKey(slug));
  } catch {
    // ignore
  }
}
